import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import device from 'common/support/device';
import intersectionWith from 'lodash/intersectionWith';

import { fetchShortCodeQrCode, fetchContactUsQrCode } from 'actions/qrCode';

import { useCurrentSubscriptions } from 'hooks/auth/subscriptions';
import { Providers, Provider } from 'app-types';
import { useGeoInfo } from 'hooks/geoInfo';
import { useIntl, MessageDescriptor } from 'react-intl';
import { appendEmailBody, getLocation } from 'support/feedbackEmailHelper';
import { VideoCtvRedux } from '../../store-types';

interface QrCodeResult {
  qrCodeUrl: string | null;
}

export const useShortCodeQrCode = (
  url: string | null,
  shortCode: string | null,
): QrCodeResult => {
  const dispatch = useDispatch();

  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchQrCodeUrl = async () => {
      const results = await dispatch(fetchShortCodeQrCode(url, shortCode) as any);

      if (results) {
        setQrCodeUrl(results);
      }
    };

    if (url && shortCode) {
      fetchQrCodeUrl();
    }
  }, [dispatch, shortCode, url]);

  return { qrCodeUrl };
};

const useAuthenticatedProviders = (): Provider[] => {
  const currentSubscriptions = useCurrentSubscriptions();

  const providers = intersectionWith(
    currentSubscriptions,
    ['adobe', 'paramountplus'],
    (a, b) => a.toLowerCase() === b.toLowerCase(),
  );

  return providers;
};

const messages: Record<string, MessageDescriptor> = {
  subject: {
    id: 'contactUs.emailsubject',
    description: 'Subject of feedback email',
  },
  customerName: {
    id: 'contactUs.emailbody.customerName',
    description: 'Customer Name in feedback email body',
  },
  deviceModel: {
    id: 'contactUs.emailbody.deviceModel',
    description: 'Device model in device type in feedback email body',
  },
  deviceType: {
    id: 'contactUs.emailbody.deviceType',
    description: 'Device type in feedback email body',
  },
  deviceOSVersion: {
    id: 'contactUs.emailbody.deviceOSVersion',
    description: 'Device OS version in feedback email body',
  },
  deviceSdkVersion: {
    id: 'contactUs.emailbody.deviceSdkVersion',
    description: 'Device SDK version in feedback email body',
  },
  appVersion: {
    id: 'contactUs.emailbody.appVersion',
    description: 'App version in feedback email body',
  },
  pPlus: {
    id: 'contactUs.emailbody.pPlus',
    description: 'Paramount Plus Account in feedback email body',
  },
  tvProvider: {
    id: 'contactUs.emailbody.tvProvider',
    description: 'TV provider in feedback email body',
  },
  customerIp: {
    id: 'contactUs.emailbody.customerIp',
    description: 'Customer IP in feedback email body',
  },
  location: {
    id: 'contactUs.emailbody.location',
    description: 'location in feedback email body',
  },
  dma: {
    id: 'contactUs.emailbody.dma',
    description: 'DMA in feedback email body',
  },
  description: {
    id: 'contactUs.emailbody.description',
    description: 'Description divider in feedback email body',
  },
};

export const useFeedBackQrCode = (): QrCodeResult => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);

  const isQrCodeUrlCreated = useRef(false);

  const subject = intl.formatMessage(messages.subject);

  const updateIpAddress = (res: string | null) => {
    setIpAddress(res);
  };

  const [ipAddress, setIpAddress] = useState<string | null>(null);

  const ipAddressPromise = device.getIpAddress();

  ipAddressPromise &&
    ipAddressPromise.then(res => {
      updateIpAddress(res);
    });

  const authenticatedProviders = useAuthenticatedProviders();

  const isPPlusLoggedIn = authenticatedProviders.find(
    provider => provider === Providers.PARAMOUNT_PLUS.toLowerCase(),
  );

  const isAdobeLoggedIn = authenticatedProviders.find(
    provider => provider === Providers.ADOBE.toLowerCase(),
  );

  const mvpd = useSelector((state: VideoCtvRedux) => state.auth?.adobe?.authN?.mvpd);

  const geoInfo = useGeoInfo();

  useEffect(() => {
    let emailBody = appendEmailBody(intl, '\n', messages.description);

    emailBody = appendEmailBody(intl, emailBody, messages.customerName);

    let customerIpAddress = '';
    if (geoInfo && geoInfo.ipAddress) {
      customerIpAddress = geoInfo.ipAddress;
    } else if (ipAddress) {
      // Fallback to use IP from platform specific api if geoInfo has no IP available
      customerIpAddress = ipAddress;
    }
    if (customerIpAddress !== '') {
      emailBody = appendEmailBody(intl, emailBody, messages.customerIp, {
        ip: customerIpAddress,
      });
    }

    const deviceModel = device.model
      ? intl.formatMessage(messages.deviceModel, {
          model: device.model,
        })
      : '';

    if (device.platform) {
      emailBody = appendEmailBody(intl, emailBody, messages.deviceType, {
        devicePlatform: device.platform,
        deviceModel,
      });
    }

    if (typeof device.detail === 'object' && device.detail !== null) {
      if (device.detail.OSVersion) {
        emailBody = appendEmailBody(intl, emailBody, messages.deviceOSVersion, {
          OSVersion: device.detail.OSVersion,
        });
      }

      if (device.detail.sdkVersion) {
        emailBody = appendEmailBody(
          intl,
          emailBody,
          messages.deviceSdkVersion,
          { sdkVersion: device.detail.sdkVersion },
        );
      }
    }

    emailBody = appendEmailBody(intl, emailBody, messages.appVersion, {
      appVersion: process.env.REACT_APP_VERSION,
    });

    if (geoInfo) {
      const location = getLocation(
        geoInfo.city,
        geoInfo.region,
        geoInfo.country,
      );
      if (location !== '') {
        emailBody = appendEmailBody(intl, emailBody, messages.location, {
          location,
        });
      }

      if (geoInfo.dma) {
        emailBody = appendEmailBody(intl, emailBody, messages.dma, {
          dma: geoInfo.dma,
        });
      }
    }

    if (isPPlusLoggedIn) {
      emailBody = appendEmailBody(intl, emailBody, messages.pPlus);
    }

    if (isAdobeLoggedIn) {
      emailBody = appendEmailBody(
        intl,
        emailBody,
        messages.tvProvider,
        {
          mvpd,
        },
        '',
      );
    }

    const fetchQrCodeUrl = async () => {
      const url = await dispatch(fetchContactUsQrCode(subject, emailBody) as any);

      if (url) {
        if (geoInfo || !isQrCodeUrlCreated.current) {
          setQrCodeUrl(url);
          isQrCodeUrlCreated.current = true;
        }
      }
    };

    fetchQrCodeUrl();
  }, [
    dispatch,
    ipAddress,
    isPPlusLoggedIn,
    isAdobeLoggedIn,
    mvpd,
    geoInfo,
    intl,
    subject,
  ]);

  return { qrCodeUrl };
};